<script setup>
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useVuelidate } from '@vuelidate/core'
import { required, email as emailValidator, helpers } from '@vuelidate/validators'
import axios from 'axios'

// Reactive state
const email = ref('')
const password = ref('')
const loading = ref(false)
const errormesj = ref('')
const typeMsj = ref('')

const router = useRouter()
const route = useRoute()

// Validation rules
const rules = {
  email: {
    required: helpers.withMessage('Email is required', required),
    email: helpers.withMessage('Please enter a valid email', emailValidator),
  },
  password: {
    required: helpers.withMessage('Password is required', required),
  },
}

// Initialize Vuelidate
const v$ = useVuelidate(rules, { email, password })

// Login function
const signinapi = async () => {
  errormesj.value = ''
  typeMsj.value = ''

  // Reset and validate form
  v$.value.$reset()
  const isValid = await v$.value.$validate()

  if (!isValid) {
    errormesj.value = 'Please correct the errors in the form'
    typeMsj.value = 'warning'
    return
  }

  loading.value = true

  try {
    const response = await axios.post(
      process.env.VUE_APP_API_BASEURL + 'login',
      {
        email: email.value,
        password: password.value,
      }
    )

    const data = response.data

    if (data.status === 'fail') {
      errormesj.value = data.message || 'Invalid credentials'
      typeMsj.value = data.type || 'danger'
      return
    }

    if (data.status === 'success') {
      // Save token and role
      localStorage.setItem('jwt', data.token)
      localStorage.setItem('role', data.role_id || '')

      // Set default auth header
      axios.defaults.headers.common['authorization'] = 'Bearer ' + data.token

      // Fetch menus using your existing endpoint
      try {
        const menuResponse = await axios.get(
          process.env.VUE_APP_API_BASEURL + 'menu/my'
        )

        if (menuResponse.data) {
          localStorage.setItem('menus', JSON.stringify(menuResponse.data))
          console.log(menuResponse.data)
        }
      } catch (menuError) {
        console.warn('Menu load failed (non-critical):', menuError)
        // Still proceed to dashboard
      }

      // Redirect
      const redirectPath = route.query.redirectFrom || '/'
      router.push(redirectPath)
    }
  } catch (error) {
    console.error('Login failed:', error)
    errormesj.value =
      error.response?.data?.message ||
      'Unable to connect. Please check your internet connection.'
    typeMsj.value = 'danger'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="auth-page-wrapper pt-5">
    <!-- Background -->
    <div class="auth-one-bg-position auth-one-bg" id="auth-particles">
      <div class=""></div>
      <div class="shape">
        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" xmlns:xlink="http://www.w3.org/1999/xlink"
          viewBox="0 0 1440 120">
          <path d="M 0,36 C 144,53.6 432,123.2 720,124 C 1008,124.8 1296,56.8 1440,40L1440 140L0 140z"></path>
        </svg>
      </div>
    </div>

    <!-- Content -->
    <div class="auth-page-content">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-md-8 col-lg-6 col-xl-5">
            <div class="card mt-4">
              <div class="card-body p-4">
                <div class="text-center mt-2">
                  <router-link to="/" class="d-inline-block auth-logo">
                    <img src="@/assets/images/renewp_logo.png" alt="Renew+ Logo" height="60" />
                  </router-link>
                  <br /><br />
                  <p class="text-muted">Sign in to continue with Renew+.</p>
                </div>

                <div class="p-2 mt-4">
                  <!-- General error/success alert -->
                  <div v-if="errormesj" class="alert" :class="{
                    'alert-warning': typeMsj === 'warning',
                    'alert-danger': typeMsj === 'danger',
                    'alert-success': typeMsj === 'success'
                  }" role="alert">
                    <i class="ri-alert-line me-3 align-middle fs-16"></i>
                    <strong>{{ errormesj }}</strong>
                  </div>

                  <!-- Email verification success -->
                  <div v-if="route.query.confirmation" class="alert alert-success alert-border-left">
                    <i class="ri-check-line me-3 align-middle fs-16"></i>
                    <strong>Email successfully verified</strong>
                  </div>

                  <form @submit.prevent="signinapi">
                    <!-- Email Field -->
                    <div class="mb-3">
                      <label for="email" class="form-label">Email</label>
                      <input
                        type="email"
                        class="form-control"
                        id="email"
                        placeholder="Enter email"
                        v-model="email"
                        :class="{ 'is-invalid': v$.email.$error }"
                      />
                      <div v-if="v$.email.$error" class="invalid-feedback">
                        {{ v$.email.$errors[0].$message }}
                      </div>
                    </div>

                    <!-- Password Field -->
                    <div class="mb-3">
                      <div class="float-end">
                        <router-link to="/forgot-password" class="text-muted">Forgot password?</router-link>
                      </div>
                      <label class="form-label" for="password-input">Password</label>
                      <div class="position-relative auth-pass-inputgroup">
                        <input
                          type="password"
                          class="form-control pe-5"
                          id="password-input"
                          placeholder="Enter password"
                          v-model="password"
                          :class="{ 'is-invalid': v$.password.$error }"
                        />
                        <div v-if="v$.password.$error" class="invalid-feedback">
                          {{ v$.password.$errors[0].$message }}
                        </div>
                      </div>
                    </div>

                    <!-- Remember me -->
                    <div class="form-check mb-3">
                      <input class="form-check-input" type="checkbox" id="auth-remember-check" />
                      <label class="form-check-label" for="auth-remember-check">Remember me</label>
                    </div>

                    <!-- Submit Button -->
                    <div class="mt-4">
                      <button
                        class="btn btn-success w-100"
                        type="submit"
                        :disabled="loading"
                      >
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        {{ loading ? 'Signing in...' : 'Sign In' }}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <div class="row">
          <div class="col-lg-12">
            <div class="text-center">
              <p class="mb-0 text-muted">
                © {{ new Date().getFullYear() }} CompAsia. All Rights Reserved
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.auth-logo img {
  max-height: 60px;
}
</style>